import { z } from 'zod/v4';
import { zodBigNumber, zodPlainDate } from '~/schemas/custom-schemas';
import { countryEnum, subsidyRegimeEnum, technologyEnum } from '~/schemas/enums';
import { gridData } from '~/schemas/grid';
import { dealTrancheVolumeTypeEnum } from './deals.schemas';

export const SupplyVolumeTypeEnum = z.enum(['CONTRACTED', 'INVENTORY', 'DISPATCHED']);

const allocationsGridDataSchema = z.object({
  allocation_batch_friendly_id: z.number().int().nullable(),
  allocation_batch_note: z.string().nullable(),
  allocation_quantity: zodBigNumber.nullable(),
  allocation_run_date: zodPlainDate.nullable(),
  allocation_run_friendly_id: z.number().int(),
  allocation_run_note: z.string().nullable(),
  allocation_status: z.string().nullable(),
  allocation_step_name: z.string().nullable(),
  allocation_step_position: z.number().int().nullable(),
  demand_certificate_label: z.array(z.string()).nullable(),
  demand_certificate_type: z.string().nullable(),
  demand_contract_type: z.string().nullable(),
  demand_counterparty_client_reference: z.string().nullable(),
  demand_counterparty_name: z.string().nullable(),
  demand_country: z.array(countryEnum).nullable(),
  demand_deal_client_reference: z.string().nullable(),
  demand_deal_tranche_client_reference: z.string().nullable(),
  demand_deal_tranche_volume_type: dealTrancheVolumeTypeEnum.nullable(),
  demand_max_delivery_month: z.number().int().nullable(),
  demand_max_delivery_year: z.number().int().nullable(),
  demand_minimum_commercial_operation_date_month: z.number().int().nullable(),
  demand_minimum_commercial_operation_date_year: z.number().int().nullable(),
  demand_period_start_month: z.number().int().nullable(),
  demand_production_device_client_reference: z.string().nullable(),
  demand_production_device_name: z.string().nullable(),
  demand_production_device_registry_identifier: z.string().nullable(),
  demand_period_start_year: z.number().int().nullable(),
  demand_sub_national_region: z.array(z.string()).nullable(),
  demand_subsidy_regime: z.array(subsidyRegimeEnum).nullable(),
  demand_supra_national_region: z.array(z.string()).nullable(),
  demand_technology: z.array(technologyEnum).nullable(),
  supply_certificate_label: z.array(z.string()).nullable(),
  supply_certificate_type: z.string().nullable(),
  supply_contract_type: z.string().nullable(),
  supply_counterparty_client_reference: z.string().nullable(),
  supply_counterparty_name: z.string().nullable(),
  supply_country: z.array(countryEnum).nullable(),
  supply_deal_client_reference: z.string().nullable(),
  supply_deal_tranche_client_reference: z.string().nullable(),
  supply_deal_tranche_volume_type: dealTrancheVolumeTypeEnum.nullable(),
  supply_max_delivery_month: z.number().int().nullable(),
  supply_max_delivery_year: z.number().int().nullable(),
  supply_minimum_commercial_operation_date_month: z.number().int().nullable(),
  supply_minimum_commercial_operation_date_year: z.number().int().nullable(),
  supply_period_start_month: z.number().int().nullable(),
  supply_period_start_year: z.number().int().nullable(),
  supply_production_device_client_reference: z.string().nullable(),
  supply_production_device_name: z.string().nullable(),
  supply_production_device_registry_identifier: z.string().nullable(),
  supply_registry: z.string().nullable(),
  supply_registry_account_holder_identifier: z.string().nullable(),
  supply_registry_account_holder_name: z.string().nullable(),
  supply_registry_account_identifier: z.string().nullable(),
  supply_registry_account_name: z.string().nullable(),
  supply_sub_national_region: z.array(z.string()).nullable(),
  supply_subsidy_regime: z.array(subsidyRegimeEnum).nullable(),
  supply_supra_national_region: z.array(z.string()).nullable(),
  supply_technology: z.array(technologyEnum).nullable(),
  supply_type: SupplyVolumeTypeEnum.nullable(),
});

export type AllocationsGridMetricsData = z.infer<typeof allocationsGridDataSchema>;

export const allocationsPivotTableResponseSchema = gridData(
  z.object({
    count: z.number(),
    data: z.array(allocationsGridDataSchema.loose()), // .loose() to allow for tags
  }),
);

export const allocationsPivotTableQueryParamsSchema = z.object({
  period_start_from: z.string(),
  period_start_to: z.string(),
  demand_period_start_from: z.string().optional(),
  demand_period_start_to: z.string().optional(),
});

export type AllocationsQueryParams = z.infer<typeof allocationsPivotTableQueryParamsSchema>;
